import * as React from 'react'
import * as va from '@vercel/analytics'
import ph from 'posthog-js'
import * as PropertiesPanel from '../../properties-panel'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import {
  RichTextLayouts,
  normalizeStringPath,
  type Block,
  type RichTextBlock,
} from '@basehub/data/replicache/index'
import { DocumentSidebarPortal } from '../../document-sidebar-portal'
import { Root } from '../../properties-panel'
import { useSlashCommandStore } from '~/lib/tiptap/extensions/rich-text/extensions/slash-command'
import { getBlockDisplayData } from '@basehub/data/replicache/mutators/repo-space/block/helpers/component-helpers'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { BlockIcon } from '~/components/block-icon'
import {
  RichTextOption,
  optionToNewOptions,
  richTextOptions,
} from '../../../../../../../../../../../../../packages/tiptap-api-utils/src/options'
import { RichTextOptionIcon } from './option-icon'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useBranchOrCommitSegment } from '~/lib/commits/use-selected-branch-or-commit'
import { useEditorStore } from '../../editor-store'
import { Button } from '~/sotano-kit/primitives/button'
import { useToggleState } from '~/hooks/use-toggle-state'
import { ConvertToConfirmationDialog } from './confirmation-dialog'
import { HamburgerMenuIcon, MinusIcon } from '@radix-ui/react-icons'
import {
  jsonToPlainText,
  richTextExtensionsForTransformations,
  stateToJson,
} from '@basehub/tiptap-api-utils'
import { getId } from '@basehub/utils/id/index'
import { toast } from 'sonner'
import { MutatorUserError } from '@basehub/data/replicache/error-handling'
import { useEditorMode } from '../../editor-mode-provider'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { getBlockFocusOp } from '~/replicache/frontend/undo-redo/util'
import {
  CreateBlockParams,
  UpdateBlockParams,
} from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { ChangeBlockTypeParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/change'
import { useTabsStore } from '../../tabs/store'
import { useRepoStore } from '../../repo-store'
import { getAllowedFormatting } from './_utils'

export const RichTextBlockPropertiesPanel = ({
  block,
  parent,
  path,
}: {
  block: RichTextBlock
  parent: Block
  path: string
}) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const { teamSlug, repoSlug } = useDashboardSlugs()
  const blockTreeManager = useWorkingTreeStore((s) => s.tree)
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const setSelectedBlockId = useEditorStore((s) => s.setSelectedBlockId)
  const undoManager = useRepoUndoManager()
  const { encodedSegment } = useBranchOrCommitSegment()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const addTab = useTabsStore((s) => s.addTab)
  const setFocus = useRepoStore((s) => s.setFocus)
  const sourceBlockId = idToSourceBlockId(block)
  const getInputFocusOp = React.useCallback(
    (id: string) => {
      return getBlockFocusOp({
        block: {
          id: block.id,
          sourceBlockId: block.sourceBlockId,
        },
        preferredTabKey: activeTabKey,
        focusType: 'block:properties-panel',
        elementIdOverride: id,
      })
    },
    [block.id, block.sourceBlockId, activeTabKey]
  )

  const allowedFormatting = getAllowedFormatting(block.constraints)
  const currentlyAllowedOptions = allowedFormatting.map(optionToNewOptions)

  const { setAcceptedComponents, components } = useSlashCommandStore()

  React.useEffect(() => {
    setAcceptedComponents({
      richTextId: block.id,
      acceptedComponents: block.constraints.allowedComponents ?? null,
    })
  }, [block.id, block.constraints.allowedComponents, setAcceptedComponents])

  const toggleState = useToggleState()
  const editorMode = useEditorMode()

  const handleConvert = React.useCallback(
    async (hasConfirmed: boolean) => {
      if (editorMode !== 'write:all') return

      if (
        hasConfirmed ||
        // editor is empty
        jsonToPlainText(
          stateToJson(block.value.state),
          richTextExtensionsForTransformations
        ).trim().length === 0
      ) {
        const params: ChangeBlockTypeParams = {
          blockId: block.id,
          title: block.title,
          path: path,
          isoNow: new Date().toISOString(),
          typeOptions: {},
          idSeed: getId(),
          newBlockType: 'text',
        }
        const result = await replicache?.mutate
          .changeBlockType(params)
          .catch((err) => {
            if (err instanceof MutatorUserError) {
              toast.error(`Failed to convert block: ${err.reason}`)
            } else {
              console.error(err)
              toast.error('Failed to convert block')
            }
          })

        if (result?.undo) {
          undoManager.current?.push({
            redo: [
              getInputFocusOp(`el-${sourceBlockId}-pp-convert-to-plain-text`),
              { op: 'changeBlockType', args: params },
            ],
            undo: [
              getInputFocusOp(`el-${sourceBlockId}-pp-convert-to-plain-text`),
              {
                op: 'blockBatchMutations',
                args: { mutations: result.undo },
              },
            ],
          })
        }
        setSelectedBlockId(sourceBlockId)
        va.track('Rich text converted to plain text')
        ph.capture('Rich text converted to plain text')
        return
      }
      toggleState.handleOn()
    },
    [
      block.id,
      block.title,
      block.value.state,
      editorMode,
      getInputFocusOp,
      path,
      replicache?.mutate,
      setSelectedBlockId,
      sourceBlockId,
      toggleState,
      undoManager,
    ]
  )

  if (!blockTreeManager) return <></>
  return (
    <>
      <PropertiesPanel.PropertiesContainer>
        <PropertiesPanel.Group title="Layout" spacing="tight">
          <PropertiesPanel.LayoutOption
            direction="vertical"
            options={[
              {
                id: 'multi-line' satisfies RichTextLayouts,
                title: 'Multi-line',
                icon: <HamburgerMenuIcon width={18} height={18} />,
                description: 'For detailed content',
              },
              {
                id: 'single-line' satisfies RichTextLayouts,
                title: 'Single-line',
                icon: <MinusIcon width={18} height={18} />,
                description: 'For titles and short texts',
              },
            ]}
            value={block.constraints.layout ?? 'multi-line'}
            id={`el-${sourceBlockId}-pp-layout`}
            onValueChange={async (v) => {
              if (!replicache) return

              const params: UpdateBlockParams = {
                id: block.id,
                instanceChildTarget: block.instanceChildTarget,
                path,
                data: {
                  type: 'rich-text',
                  constraints: { layout: v.id as RichTextLayouts },
                },
                strategy: 'merge-deep',
                isoNow: new Date().toISOString(),
              }
              const result = await replicache.mutate.updateBlock(params)

              if (result?.undo) {
                undoManager.current?.push({
                  redo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-layout`),
                    { op: 'updateBlock', args: params },
                  ],
                  undo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-layout`),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                  ],
                })
              }
            }}
          />
        </PropertiesPanel.Group>
        <PropertiesPanel.Group title="Constraints" spacing="normal">
          <PropertiesPanel.BooleanOption
            name="Is required"
            value={block.constraints.isRequired}
            id={`el-${sourceBlockId}-pp-is-required`}
            mutate={async (v) => {
              const params: UpdateBlockParams = {
                id: block.id,
                instanceChildTarget: block.instanceChildTarget,
                data: {
                  constraints: { isRequired: v },
                },
                path,
                strategy: 'merge-deep',
                isoNow: new Date().toISOString(),
              }
              const result = await replicache?.mutate.updateBlock(params)
              if (result?.undo) {
                undoManager.current?.push({
                  redo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-is-required`),
                    { op: 'updateBlock', args: params },
                  ],
                  undo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-is-required`),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                  ],
                })
              }
            }}
          />
          <PropertiesPanel.AllowedItemsOption
            id={`el-${sourceBlockId}-pp-allowed-items`}
            options={richTextOptions.map((richTextOption) => {
              return {
                name: richTextOption,
                icon: <RichTextOptionIcon name={richTextOption} />,
                allowed: currentlyAllowedOptions.some(
                  (o) => o === richTextOption
                ),
              }
            })}
            title="Formatting"
            handleClear={async () => {
              const params: UpdateBlockParams = {
                id: block.id,
                instanceChildTarget: block.instanceChildTarget,
                data: {
                  type: block.type,
                  constraints: {
                    forbiddenNodes: 'all',
                  },
                },
                path,
                strategy: 'merge-deep',
                isoNow: new Date().toISOString(),
              }
              const result = await replicache?.mutate.updateBlock(params)
              if (result?.undo) {
                undoManager.current?.push({
                  redo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-allowed-items`),
                    { op: 'updateBlock', args: params },
                  ],
                  undo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-allowed-items`),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                  ],
                })
              }
            }}
            handleSelectAll={async () => {
              const params: UpdateBlockParams = {
                id: block.id,
                instanceChildTarget: block.instanceChildTarget,
                data: {
                  type: block.type,
                  constraints: {
                    forbiddenNodes: null,
                  },
                },
                path,
                strategy: 'merge-deep',
                isoNow: new Date().toISOString(),
              }
              const result = await replicache?.mutate.updateBlock(params)
              if (result?.undo) {
                undoManager.current?.push({
                  redo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-allowed-items`),
                    { op: 'updateBlock', args: params },
                  ],
                  undo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-allowed-items`),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                  ],
                })
              }
            }}
            handleChange={async (name, enabled) => {
              const blacklist: string[] = [
                ...(block.constraints.forbiddenNodes === 'all'
                  ? richTextOptions
                  : (block.constraints.forbiddenNodes ?? [])),
              ]

              if (enabled) {
                // now it should be PERMITTED ⇒ remove from blacklist
                const idx = blacklist.indexOf(name as RichTextOption)
                if (idx !== -1) blacklist.splice(idx, 1)
              } else {
                // now it should be BLOCKED ⇒ add if not already
                if (!blacklist.includes(name as RichTextOption)) {
                  blacklist.push(name as RichTextOption)
                }
              }

              // sort consistent with richTextOptions
              blacklist.sort(
                (a, b) =>
                  richTextOptions.indexOf(a) - richTextOptions.indexOf(b)
              )

              const params: UpdateBlockParams = {
                id: block.id,
                instanceChildTarget: block.instanceChildTarget,
                data: {
                  type: block.type,
                  constraints: {
                    forbiddenNodes: blacklist.length ? blacklist : null, // null = allow-all
                  },
                },
                path,
                strategy: 'merge-deep',
                isoNow: new Date().toISOString(),
              }

              const result = await replicache?.mutate.updateBlock(params)
              if (result?.undo) {
                undoManager.current?.push({
                  redo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-allowed-items`),
                    { op: 'updateBlock', args: params },
                  ],
                  undo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-allowed-items`),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                  ],
                })
              }
            }}
          />

          {/* ------------------------------------------------------------------------------------------------
            // References
            // ------------------------------------------------------------------------------------------------*/}
          <PropertiesPanel.CheckList
            className="pt-1"
            itemName="Components"
            id={`el-${sourceBlockId}-pp-allowed-components`}
            options={components.map((o) => {
              const { displayTitle } = getBlockDisplayData({
                block: o,
                parentBlock: parent,
              })
              return {
                name: displayTitle,
                id: idToSourceBlockId(o),
                href: `/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${idToSourceBlockId(
                  o
                )}`,
                icon: (
                  <BlockIcon
                    block={{ type: o.type }}
                    iconProps={{ width: 14, height: 14 }}
                  />
                ),
              }
            })}
            checkedOptions={components
              .filter((o) =>
                block.constraints.allowedComponents?.includes(
                  idToSourceBlockId(o)
                )
              )
              .map((o) => {
                const { displayTitle } = getBlockDisplayData({
                  block: o,
                  parentBlock: parent,
                })
                return {
                  name: displayTitle,
                  id: idToSourceBlockId(o),
                  href: `/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${idToSourceBlockId(
                    o
                  )}`,
                  icon: (
                    <BlockIcon
                      block={{ type: o.type }}
                      iconProps={{ width: 14, height: 14 }}
                    />
                  ),
                }
              })}
            title="Component types"
            mutate={async (mode, options) => {
              const currentAcceptedTypes = [
                ...(block.constraints.allowedComponents ?? []),
              ]
              let newAcceptedTypes: string[] | null
              if (mode === 'overwrite') {
                newAcceptedTypes = options.map((option) => option.id)
              } else {
                newAcceptedTypes = mode
                  ? currentAcceptedTypes.concat(
                      options.map((option) => option.id)
                    )
                  : currentAcceptedTypes.filter(
                      (t) => !options.some((option) => t === option.id)
                    )
              }

              if (newAcceptedTypes.length === 0) {
                newAcceptedTypes = null
              }

              const params: UpdateBlockParams = {
                id: block.id,
                instanceChildTarget: block.instanceChildTarget,
                data: {
                  type: block.type,
                  constraints: {
                    allowedComponents: newAcceptedTypes,
                  },
                },
                path,
                strategy: 'merge-deep',
                isoNow: new Date().toISOString(),
              }
              const result = await replicache?.mutate.updateBlock(params)
              if (result?.undo) {
                undoManager.current?.push({
                  redo: [
                    getInputFocusOp(
                      `el-${sourceBlockId}-pp-allowed-components`
                    ),
                    { op: 'updateBlock', args: params },
                  ],
                  undo: [
                    getInputFocusOp(
                      `el-${sourceBlockId}-pp-allowed-components`
                    ),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                  ],
                })
              }
            }}
            addNew={{
              label: 'Add new',
              onClick: async () => {
                // will create an "internal" component
                // "internal" means its below a reference and it cannot be referenced by other references
                const newComponentId = getId()
                const params: CreateBlockParams = {
                  idSeed: getId(),
                  data: {
                    id: newComponentId,
                    type: 'component',
                    hidden: true,
                  },
                  path: normalizeStringPath({
                    path,
                    edgeId: block.id,
                    format: 'string',
                  }),
                  parentInstanceChildTarget: block.instanceChildTarget,
                  isoNow: new Date().toISOString(),
                }
                const result = await replicache?.mutate.createBlock(params)
                if (result?.undo) {
                  undoManager.current?.push({
                    redo: [
                      getInputFocusOp(
                        `el-${sourceBlockId}-pp-allowed-components`
                      ),
                      { op: 'createBlock', args: params },
                    ],
                    undo: [
                      getInputFocusOp(
                        `el-${sourceBlockId}-pp-allowed-components`
                      ),
                      {
                        op: 'blockBatchMutations',
                        args: { mutations: result.undo },
                      },
                    ],
                  })
                }
                addTab({ key: newComponentId, partiallyOpen: true })
                const activeDocument = activeTabKey
                  ? getNode?.(activeTabKey, {
                      fallbackKey: 'sourceBlockId',
                    })
                  : null
                setFocus({
                  type: 'block:document-title',
                  blockId: newComponentId,
                  temporaryBackLink: {
                    title:
                      activeDocument?.value?.title ??
                      (block.title || 'Untitled'),
                    id: idToSourceBlockId(activeDocument?.value ?? block),
                    edgeId:
                      activeDocument && activeDocument.value.id !== block.id
                        ? idToSourceBlockId(block)
                        : undefined,
                  },
                })
              },
            }}
          />

          <PropertiesPanel.CheckList
            className="pt-1 !mt-2"
            itemName="Components"
            id={`el-${sourceBlockId}-pp-allowed-references`}
            options={components.map((o) => {
              const { displayTitle } = getBlockDisplayData({
                block: o,
                parentBlock: parent,
              })
              return {
                name: displayTitle,
                id: idToSourceBlockId(o),
                href: `/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${idToSourceBlockId(
                  o
                )}`,
                icon: (
                  <BlockIcon
                    block={{ type: o.type }}
                    iconProps={{ width: 14, height: 14 }}
                  />
                ),
              }
            })}
            checkedOptions={components
              .filter((o) =>
                block.constraints.allowedInternalLinks?.includes(
                  idToSourceBlockId(o)
                )
              )
              .map((o) => {
                const { displayTitle } = getBlockDisplayData({
                  block: o,
                  parentBlock: parent,
                })
                return {
                  name: displayTitle,
                  id: idToSourceBlockId(o),
                  href: `/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${idToSourceBlockId(
                    o
                  )}`,
                  icon: (
                    <BlockIcon
                      block={{ type: o.type }}
                      iconProps={{ width: 14, height: 14 }}
                    />
                  ),
                }
              })}
            title="Allowed internal links"
            mutate={async (mode, options) => {
              const currentAcceptedTypes = [
                ...(block.constraints.allowedInternalLinks ?? []),
              ]
              let newAcceptedTypes: string[] | null = null
              if (mode === 'overwrite') {
                newAcceptedTypes = options.map((option) => option.id)
              } else {
                newAcceptedTypes = mode
                  ? currentAcceptedTypes.concat(
                      options.map((option) => option.id)
                    )
                  : currentAcceptedTypes.filter(
                      (t) => !options.some((option) => t === option.id)
                    )
              }

              if (newAcceptedTypes.length === 0) {
                newAcceptedTypes = null
              }

              const params: UpdateBlockParams = {
                id: block.id,
                instanceChildTarget: block.instanceChildTarget,
                data: {
                  type: block.type,
                  constraints: {
                    allowedInternalLinks: newAcceptedTypes,
                  },
                },
                path,
                strategy: 'merge-deep',
                isoNow: new Date().toISOString(),
              }
              const result = await replicache?.mutate.updateBlock(params)
              if (result?.undo) {
                undoManager.current?.push({
                  redo: [
                    getInputFocusOp(
                      `el-${sourceBlockId}-pp-allowed-references`
                    ),
                    { op: 'updateBlock', args: params },
                  ],
                  undo: [
                    getInputFocusOp(
                      `el-${sourceBlockId}-pp-allowed-references`
                    ),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                  ],
                })
              }
            }}
          />
        </PropertiesPanel.Group>

        <PropertiesPanel.CommonSearchConfigGroup
          block={block}
          parent={parent}
          path={path}
        />
        {editorMode === 'write:all' && (
          <PropertiesPanel.ConfigureContainer block={block}>
            <PropertiesPanel.Group noBorderBottom>
              <Button
                intent="secondary"
                onClick={() => handleConvert(false)}
                id={`el-${sourceBlockId}-pp-convert-to-plain-text`}
                icon={
                  <BlockIcon
                    block={{ type: 'text' }}
                    iconProps={{ className: 'mr-0.5' }}
                  />
                }
              >
                Convert to Plain Text
              </Button>
            </PropertiesPanel.Group>
          </PropertiesPanel.ConfigureContainer>
        )}
        <ConvertToConfirmationDialog
          toggleState={toggleState}
          block={block}
          onConfirm={() => handleConvert(true)}
        />
      </PropertiesPanel.PropertiesContainer>
    </>
  )
}

export const FullViewPropertiesPanelWrapper = ({
  block,
  children,
  path,
  parentBlock,
}: {
  block: RichTextBlock
  children: React.ReactNode
  path: string
  parentBlock: Block
}) => {
  const isSelected = useEditorStore((s) => {
    return !s.selectedBlockId || s.selectedBlockId === idToSourceBlockId(block)
  })

  return (
    <DocumentSidebarPortal hidden={!isSelected}>
      <Root block={block} parentBlock={parentBlock}>
        <PropertiesPanel.BlockTitleGroup block={block} path={path} />
        {children}
      </Root>
    </DocumentSidebarPortal>
  )
}
